import * as types from "./mutation-types";
import { IState } from "@/types/IState";
import { IRestoreTimer } from "@/types/IRestoreTimer";
import { IWebhookSetting } from "@/types/IWebhookSetting";
import { PomodoroStatus } from "@/enums/PomodoroStatus";
import firebase from "@/firebase";
import axios from "axios";

function getNextStatus(state: IState) {
    const nextStatus = {
        totalTime: 0,
        currentStatus: 0,
        workCount: state.workCount,
    };
    switch (state.currentStatus) {
        case PomodoroStatus.WORK: {
            nextStatus.workCount++;
            if (nextStatus.workCount % 4 === 0) {
                nextStatus.totalTime = state.longBreakMinutes * 60;
                nextStatus.currentStatus = PomodoroStatus.LONG_BREAK;
            } else {
                nextStatus.totalTime = state.shortBreakMinutes * 60;
                nextStatus.currentStatus = PomodoroStatus.SHORT_BREAK;
            }
            break;
        }
        case PomodoroStatus.SHORT_BREAK:
        case PomodoroStatus.LONG_BREAK: {
            nextStatus.totalTime = state.workMinutes * 60;
            nextStatus.currentStatus = PomodoroStatus.WORK;
            break;
        }
    }
    return nextStatus;
}

export default {
    startTimer: (context: any) => {
        const commit = context.commit;
        const state: IState = context.state;
        const dispatch = context.dispatch;
        const timerInterval = window.setInterval(() => {
            if (state.totalTime === 0) {
                commit(types.CHANGE_TIMER_STATUS, getNextStatus(state));
            } else {
                commit(types.COUNTDOWN_TIMER);
            }
            dispatch("save");
        }, 1000);
        commit(types.START_TIMER, { timerInterval });
        commit(types.PREPARE_SOUND);
    },

    pauseTimer: (context: any) => {
        const commit = context.commit;
        const state: IState = context.state;
        if (state.timerInterval) {
            clearInterval(state.timerInterval);
        }
        commit(types.PAUSE_TIMER);
    },

    resetTimer: (context: any) => {
        const commit = context.commit;
        const state: IState = context.state;
        const dispatch = context.dispatch;
        if (state.timerInterval) {
            clearInterval(state.timerInterval);
        }
        commit(types.RESET_TIMER);
        dispatch("clearData");
    },

    save: (context: any) => {
        const state: IState = context.state;
        const restoreData: IRestoreTimer = {
            currentStatus: state.currentStatus,
            totalTime: state.totalTime,
            workCount: state.workCount,
            workMinutes: state.workMinutes,
            shortBreakMinutes: state.shortBreakMinutes,
            longBreakMinutes: state.longBreakMinutes,
            soundEnabled: state.soundEnabled,
            vibrationEnabled: state.vibrationEnabled
        };
        const data = JSON.stringify(restoreData);
        window.localStorage.setItem("POMODORO_DATA", data);
    },

    restore: (context: any) => {
        const commit = context.commit;
        const data = window.localStorage.getItem("POMODORO_DATA");
        if (data) {
            commit(types.RESTORE_TIMER, JSON.parse(data));
        }

        const notificationEnabled = firebase.checkNotificationEnabled();
        commit(types.CHANGE_NOTIFICATION_STATUS, { notificationEnabled });

        const webhookSetting = window.localStorage.getItem("POMODORO_WEBHOOK_SETTINGS");
        if (webhookSetting) {
            const webhookJson = JSON.parse(webhookSetting);
            commit(types.UPDATE_WEBHOOK_METHOD, webhookJson.method);
            commit(types.UPDATE_WEBHOOK_URL, webhookJson.url);
            commit(types.UPDATE_WEBHOOK_HEADERS, webhookJson.headers);
            commit(types.UPDATE_WEBHOOK_DATA, webhookJson.data);
        }
    },

    clearData: () => {
        window.localStorage.removeItem("POMODORO_DATA");
    },

    requestUserNotificationPermission: async (context: any) => {
        const commit = context.commit;
        const token = await firebase.requestUserNotificationPermission();
        if (token) {
            commit(types.CHANGE_NOTIFICATION_STATUS, { notificationEnabled: !!token });
        }
    },

    changeWakeLockStatus: (context: any) => {
        const commit = context.commit;
        commit(types.CHANGE_WAKE_LOCK_STATUS);
    },

    changeSoundStatus: (context: any) => {
        const commit = context.commit;
        commit(types.CHANGE_SOUND_STATUS);
    },

    changeVibrationStatus: (context: any) => {
        const commit = context.commit;
        commit(types.CHANGE_VIBRATION_STATUS);
    },

    resetTimerSettings: (context: any) => {
        const commit = context.commit;
        const dispatch = context.dispatch;
        commit(types.RESET_TIMER_SETTINGS);
        dispatch("resetWebhookSettings");
    },

    updateWorkMinutes: (context: any, workMinutes: number) => {
        const commit = context.commit;
        const state: IState = context.state;
        commit(types.UPDATE_WORK_MINUTES, { workMinutes });
        if (state.currentStatus === PomodoroStatus.WORK) {
            commit(types.UPDATE_TOTAL_TIME, { totalTime: workMinutes * 60 });
        }
    },

    updateShortBreakMinutes: (context: any, shortBreakMinutes: number) => {
        const commit = context.commit;
        const state: IState = context.state;
        commit(types.UPDATE_SHORT_BREAK_MINUTES, { shortBreakMinutes });
        if (state.currentStatus === PomodoroStatus.SHORT_BREAK) {
            commit(types.UPDATE_TOTAL_TIME, { totalTime: shortBreakMinutes * 60 });
        }
    },

    updateLongBreakMinutes: (context: any, longBreakMinutes: number) => {
        const commit = context.commit;
        const state: IState = context.state;
        commit(types.UPDATE_LONG_BREAK_MINUTES, { longBreakMinutes });
        if (state.currentStatus === PomodoroStatus.LONG_BREAK) {
            commit(types.UPDATE_TOTAL_TIME, { totalTime: longBreakMinutes * 60 });
        }
    },


    playSound: (context: any) => {
        const commit = context.commit;
        commit(types.PLAY_SOUND);
    },

    playVibration: (context: any) => {
        const commit = context.commit;
        commit(types.PLAY_VIBRATION);
    },

    updateWebhookMethod: (context: any, value: string) => {
        const commit = context.commit;
        commit(types.UPDATE_WEBHOOK_METHOD, value);
    },

    updateWebhookUrl: (context: any, value: string) => {
        const commit = context.commit;
        commit(types.UPDATE_WEBHOOK_URL, value);
    },

    updateWebhookHeaders: (context: any, value: string) => {
        const commit = context.commit;
        commit(types.UPDATE_WEBHOOK_HEADERS, value);
    },

    updateWebhookData: (context: any, value: string) => {
        const commit = context.commit;
        commit(types.UPDATE_WEBHOOK_DATA, value);
    },

    saveWebhookSettings: (context: any, webhookSetting: IWebhookSetting) => {
        const commit = context.commit;
        commit(types.UPDATE_WEBHOOK_METHOD, webhookSetting.method);
        commit(types.UPDATE_WEBHOOK_URL, webhookSetting.url);
        commit(types.UPDATE_WEBHOOK_HEADERS, webhookSetting.headers);
        commit(types.UPDATE_WEBHOOK_DATA, webhookSetting.data);

        const data = JSON.stringify(webhookSetting);
        window.localStorage.setItem("POMODORO_WEBHOOK_SETTINGS", data);
    },

    tryWebhook: async (_: any, webhookSetting: IWebhookSetting) => {
        return axios({
            method: webhookSetting.method!,
            url: webhookSetting.url!,
            headers: webhookSetting.headers ? JSON.parse(webhookSetting.headers!) : null,
            data: webhookSetting.data ? JSON.parse(webhookSetting.data!) : null
        });
    },

    sendWebhook: async (context: any) => {
        const state: IState = context.state;
        if (state.webhook.method && state.webhook.url) {
            try {
                if (!/^(POST|PUT)$/.test(state.webhook.method)) {
                    throw new Error("Invalid Method.");
                }
                await axios({
                    method: state.webhook.method!,
                    url: state.webhook.url!,
                    headers: state.webhook.headers ? JSON.parse(state.webhook.headers!) : null,
                    data: state.webhook.data ? JSON.parse(state.webhook.data!) : null
                });
            } catch (err) {
                console.error(err); // eslint-disable-line
            }
        }
    },

    resetWebhookSettings: (context: any) => {
        const commit = context.commit;
        commit(types.RESET_WEBHOOK_SETTINGS);
        window.localStorage.removeItem("POMODORO_WEBHOOK_SETTINGS");
    }
};